import {
  memo,
  useRef,
  useEffect,
  useState,
  isValidElement,
  useCallback,
} from "react";
import React from "react";
import {
  Paper,
  Typography,
  Stack,
  Button,
  CircularProgress,
  Alert,
  AlertTitle,
  Box,
  Tabs,
  Tab,
} from "@mui/material";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import BiotechIcon from "@mui/icons-material/Biotech";
import ScienceIcon from "@mui/icons-material/Science";
import { styled } from "@mui/material/styles";
import { ExtractedAnalyte, ExtractedExam } from "../../hooks/types";
import { useExamExtraction } from "./hooks/useExamExtraction";
import { ExtractionDialog } from "./components/ExtractionDialog";
import { useMedicalCareContext } from "../../../../../../contexts/MedicalCareContext";
import { useFhirData } from "../../../../../../utils/useFhirData";
import { GetBinary } from "../../../../../../services/fhir/patientsummary/Binary/GetBinary";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { ExamRequestSelect } from "./components/ExamRequestSelect";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

interface ExistingExam {
  id: string;
  description: React.ReactNode;
  date: string;
  resultUrl: string;
  specialty?: string;
  practitioner?: string;
  category?: string;
}

interface PendingExamsProps {
  handleExamExtraction: (selectedAnalytes: ExtractedAnalyte[]) => Promise<any>;
  handleAddExtractedExam: (exams: ExtractedExam[]) => void;
}

export const PendingExams = memo(
  ({ handleExamExtraction, handleAddExtractedExam }: PendingExamsProps) => {
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const { summaryData } = useMedicalCareContext();
    const [existingExams, setExistingExams] = useState<ExistingExam[]>([]);
    const [pendingExams, setPendingExams] = useState<any[]>([]);
    const [selectedRequestId, setSelectedRequestId] = useState<string | null>(
      null
    );
    const [selectedTab, setSelectedTab] = useState(0);
    const [noLinking, setNoLinking] = useState(false);
    const [selectedExamTitle, setSelectedExamTitle] = useState<string | null>(
      null
    );
    const [loadingExam, setLoadingExam] = useState<string | null>(null);

    const handleExamSaved = useCallback(() => {
      setSelectedRequestId(null);
      setNoLinking(false);
    }, []);

    const {
      isVerifying,
      isExtracting,
      fileBase64,
      showDialog,
      extractedAnalytes,
      isInvalidDocument,
      extractionError,
      handleClose,
      handleFileUpload,
      handleRetryExtraction,
      handleProceedAnyway,
      handleAnalyteSelection,
      handleSelectAll,
      handleAddSelectedAnalytes,
      handleExistingExam,
      handleStartExtraction,
    } = useExamExtraction({
      handleExamExtraction,
      handleAddExtractedExam,
      selectedRequestId,
      noLinking,
      onExamSaved: handleExamSaved,
    });

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
      setSelectedTab(newValue);
      setSelectedExamTitle(null);
    };

    const isUploadEnabled = selectedRequestId || noLinking;

    useEffect(() => {
      // Obter documentos de resultado de exames do sumário
      const resultsData = useFhirData.extractPatientSummaryResource(
        summaryData,
        "DocumentReference",
        "resultado"
      );

      // Obter documentos de solicitação
      const documentReference = useFhirData
        .extractPatientSummaryResource(
          summaryData,
          "DocumentReference",
          "service-request"
        )?.[0]
        ?.entry?.filter(
          (el: any) =>
            el?.resource?.category?.[0]?.coding?.[0]?.display === "Solicitação"
        );

      // Obter exames
      const exames = useFhirData.extractPatientSummaryResource(
        summaryData,
        "ServiceRequest"
      );

      // Transformar os dados para o formato esperado
      const allExams =
        documentReference
          ?.map((doc: any) => {
            const auxPractitioner = doc?.resource?.contained?.find(
              (contained: any) => contained?.resourceType === "Practitioner"
            );

            const auxDescription = exames?.[0]?.entry?.filter(
              (request: any) => {
                const findRef = doc?.resource?.basedOn?.find((basedOn: any) =>
                  basedOn?.reference.includes(request.resource.id)
                );
                return findRef;
              }
            );

            // Se não tiver auxDescription ou se a descrição for inválida, retorna null para filtrar depois
            if (!auxDescription?.length) return null;

            const description =
              auxDescription?.[0]?.resource?.code?.concept?.coding?.[0]
                ?.display;
            // Se a descrição for inválida ou vazia, retorna null
            if (!description || description === "-") return null;

            // Processar a data
            const rawDate = doc.resource.date;
            const formattedDate = rawDate
              ? dayjs(rawDate).format("DD/MM/YYYY")
              : "-";

            // Verificar se já existe resultado para este exame
            const hasResult = resultsData?.[0]?.entry?.some((result: any) => {
              const basedOnReferences = result?.resource?.basedOn || [];
              return basedOnReferences.some((based: any) => {
                const serviceRequestId = based.reference?.split("/")[1];
                return auxDescription?.some(
                  (service: any) => service?.resource?.id === serviceRequestId
                );
              });
            });

            if (hasResult) return null;

            return {
              id: doc.resource.id,
              description: description,
              date: formattedDate,
              category:
                auxDescription?.[0]?.resource?.category?.[0]?.coding?.[0]
                  ?.display || "-",
              practitioner:
                auxPractitioner?.name?.[0]?.given?.[0] ||
                auxPractitioner?.identifier?.[0]?.value ||
                "-",
              specialty:
                doc?.resource?.practiceSetting?.coding?.[0]?.display || "-",
              fullResource: doc.resource,
            };
          })
          .filter(Boolean) || [];

      setPendingExams(allExams);

      // Obter documentos de resultado de exames do sumário
      const resultsDataExisting = useFhirData.extractPatientSummaryResource(
        summaryData,
        "DocumentReference",
        "resultado"
      );

      // Obter exames
      const examesExisting = useFhirData.extractPatientSummaryResource(
        summaryData,
        "ServiceRequest"
      );

      // Transformar os dados para o formato esperado
      const exams =
        resultsDataExisting?.[0]?.entry
          ?.map((result: any) => {
            // Encontrar os detalhes do exame na solicitação
            const auxDescription = examesExisting?.[0]?.entry?.filter(
              (request: any) => {
                const findRef = result?.resource?.basedOn?.find(
                  (basedOn: any) =>
                    basedOn?.reference.includes(request.resource.id)
                );
                return findRef;
              }
            );

            // Se não tiver auxDescription, retorna null para filtrar depois
            if (!auxDescription?.length) return null;

            // Obter informações do profissional
            const auxPractitioner = result?.resource?.contained?.find(
              (contained: any) => contained?.resourceType === "Practitioner"
            );

            const rawDate = result.resource.date;
            const formattedDate = rawDate
              ? dayjs(rawDate).format("DD/MM/YYYY")
              : "-";

            return {
              id: result.resource.id,
              description: auxDescription?.map((serviceRequest: any) => (
                <Typography key={serviceRequest.resource.id} variant="body1">
                  {
                    serviceRequest?.resource?.code?.concept?.coding?.[0]
                      ?.display
                  }
                </Typography>
              )),
              date: formattedDate,
              resultUrl: result.resource.content?.[0]?.attachment?.url,
              specialty:
                result?.resource?.practiceSetting?.coding?.[0]?.display || "-",
              practitioner:
                auxPractitioner?.name?.[0]?.given?.[0] ||
                auxPractitioner?.identifier?.[0]?.value ||
                "-",
              category:
                auxDescription?.[0]?.resource?.category?.[0]?.coding?.[0]
                  ?.display || "-",
            };
          })
          .filter(Boolean) || [];

      setExistingExams(exams);
    }, [summaryData]);

    const getTextFromDescription = (description: React.ReactNode): string => {
      if (typeof description === "string") {
        return description;
      }
      if (Array.isArray(description)) {
        return description
          .map((item) => getTextFromDescription(item))
          .join(", ");
      }
      if (isValidElement(description)) {
        const children = description.props.children;
        if (typeof children === "string") {
          return children;
        }
        if (Array.isArray(children)) {
          return children
            .map((child) => getTextFromDescription(child))
            .join(", ");
        }
      }
      return "";
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        if (selectedRequestId && !noLinking) {
          const selectedExam = pendingExams.find(
            (exam) => exam.id === selectedRequestId
          );
          setSelectedExamTitle(
            selectedExam?.description || "Exame Selecionado"
          );
        } else if (noLinking) {
          setSelectedExamTitle("Exame sem vinculação");
        }
        handleFileUpload(file);
      }
    };

    const handleExistingExamSelect = async (exam: ExistingExam) => {
      try {
        setLoadingExam(exam.id);
        const examTitle = getTextFromDescription(exam.description);
        setSelectedExamTitle(examTitle);

        const response = await GetBinary({
          urlBinary: exam.resultUrl,
        });

        const base64Data = `data:application/pdf;base64,${response.data}`;

        await handleExistingExam(base64Data);
      } catch (error) {
        console.error("Erro ao obter o documento:", error);
        toast.error("Erro ao carregar o documento. Tente novamente.");
      } finally {
        setLoadingExam(null);
      }
    };

    return (
      <Paper
        elevation={3}
        sx={{ p: 3, borderRadius: 2, flex: 1, height: "100%" }}
      >
        <>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              aria-label="Opções de extração"
            >
              <Tab
                label="EXAMES PENDENTES"
                {...a11yProps(0)}
                sx={{ textTransform: "none" }}
              />
              <Tab
                label="EXAMES REALIZADOS"
                {...a11yProps(1)}
                sx={{ textTransform: "none" }}
              />
            </Tabs>
          </Box>

          <TabPanel value={selectedTab} index={0}>
            <Stack spacing={1} sx={{ maxHeight: "400px", overflowY: "auto" }}>
              <ExamRequestSelect
                examRequests={pendingExams}
                selectedRequestId={selectedRequestId}
                onSelectRequest={setSelectedRequestId}
                noLinking={noLinking}
                onNoLinkingChange={setNoLinking}
              />

              <Typography
                variant="body1"
                color="text.secondary"
                textAlign="center"
              >
                Faça o upload do PDF de um resultado de exame laboratorial.
                Selecione a solicitação de exame correspondente ao resultado.
              </Typography>

              {isInvalidDocument && (
                <Alert
                  severity="warning"
                  sx={{
                    width: "100%",
                    "& .MuiAlert-message": { width: "100%" },
                  }}
                >
                  <AlertTitle>Atenção</AlertTitle>
                  <Stack spacing={2}>
                    <Typography>
                      Este arquivo parece não ser um resultado de exame.
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={2}
                      justifyContent="flex-end"
                    >
                      <Button size="small" onClick={handleClose}>
                        Cancelar
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleProceedAnyway}
                      >
                        Prosseguir mesmo assim
                      </Button>
                    </Stack>
                  </Stack>
                </Alert>
              )}

              {isVerifying ? (
                <Stack spacing={2} alignItems="center" justifyContent="center">
                  <CircularProgress />
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    textAlign="center"
                  >
                    Verificando documento...
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                  >
                    Aguarde enquanto validamos o arquivo
                  </Typography>
                </Stack>
              ) : !isInvalidDocument && !isExtracting ? (
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<FileUploadIcon />}
                  disabled={isExtracting || !isUploadEnabled}
                >
                  Selecionar arquivo
                  <VisuallyHiddenInput
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf"
                    onChange={handleFileChange}
                  />
                </Button>
              ) : null}
            </Stack>
          </TabPanel>

          <TabPanel value={selectedTab} index={1}>
            <Stack
              spacing={1}
              sx={{ maxHeight: "250px", overflowY: "auto", pr: 1 }}
            >
              {existingExams.length > 0 ? (
                existingExams.map((exam) => (
                  <Paper
                    key={exam.id}
                    elevation={1}
                    sx={{
                      p: 1,
                      cursor: "pointer",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    <Stack spacing={1}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                      >
                        <Box flex={1}>
                          {exam.description}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            Data de upload: {exam.date}
                          </Typography>
                          {/* <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              Categoria: {exam.category}
                            </Typography> */}
                          {/* <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              Profissional: {exam.practitioner}
                            </Typography> */}
                          {exam.specialty && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              Especialidade solicitante: {exam.specialty}
                            </Typography>
                          )}
                        </Box>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleExistingExamSelect(exam)}
                          disabled={loadingExam === exam.id}
                          startIcon={
                            loadingExam === exam.id ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : undefined
                          }
                        >
                          Visualizar
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                ))
              ) : (
                <Typography color="text.secondary" textAlign="center">
                  Nenhum resultado disponível para extração
                </Typography>
              )}
            </Stack>
          </TabPanel>

          {showDialog && (
            <ExtractionDialog
              open={showDialog}
              onClose={() => {
                handleClose();
                setSelectedExamTitle(null);
              }}
              fileBase64={fileBase64}
              onAddSelectedAnalytes={handleAddSelectedAnalytes}
              examTitle={selectedExamTitle || undefined}
              isExtracting={isExtracting}
              handleSave={handleAddSelectedAnalytes}
              extractionError={extractionError}
              extractedAnalytes={extractedAnalytes}
              onRetryExtraction={handleRetryExtraction}
              onSelectAnalyte={handleAnalyteSelection}
              onSelectAll={handleSelectAll}
              onStartExtraction={handleStartExtraction}
              viewOnly={selectedTab === 1}
            />
          )}
        </>
      </Paper>
    );
  }
);
